// ── Canvas upload merge ─────────────────────────────────────────────
// When an upload pre-fills a canvas, we must never wipe deep-dive
// content the leader has already captured. Glenn lost a day of
// narrative work the first time this wasn't guarded. The rules:
//
//   1. Underscore-prefixed keys on the existing section are NEVER
//      overwritten. They carry _narrative, _explore_transcript,
//      _evaluation and any other meta the section has accumulated.
//   2. The OM-shaped { current: {...}, future: {...} } also has its
//      sub-objects merged with the same rule — narratives inside
//      .current / .future are preserved.
//   3. Repeatable sections (Direction's customers, values,
//      differentiators, current_challenges) — if the leader already
//      has an entries array with content, the upload does NOT touch
//      it. Uploaded entries can't safely merge with manually-curated
//      entries. Upload only lands when entries is empty / missing.
//   4. For every other plain field, incoming wins (that's the
//      upload's purpose).

function isObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function mergeOneSide(existingSide, incomingSide) {
  // Merge a single object level — incoming wins for non-underscore
  // keys; underscore-prefixed keys on existing are preserved.
  if (!isObject(existingSide)) return incomingSide;
  if (!isObject(incomingSide)) return existingSide;
  const out = { ...incomingSide };
  for (const k of Object.keys(existingSide)) {
    if (k.startsWith('_')) out[k] = existingSide[k];
  }
  return out;
}

// Merge a single section's existing data with the incoming upload
// payload. Returns the merged value to save back to canvas state.
export function mergeSectionPreservingMeta(existing, incoming) {
  if (existing == null) return incoming;
  if (incoming == null) return existing;
  if (!isObject(existing) || !isObject(incoming)) return incoming;

  // Repeatable section guard. If the leader has entries already we
  // keep them entirely — uploaded entries can't merge cleanly with
  // manually curated ones (you'd end up with duplicates or worse).
  if (Array.isArray(existing.entries) && existing.entries.length > 0) {
    return existing;
  }

  // Start from incoming, then re-apply preserved bits from existing.
  let out = { ...incoming };

  // Top-level meta keys (e.g. flat Direction sections store
  // _narrative / _evaluation directly at the root).
  for (const k of Object.keys(existing)) {
    if (k.startsWith('_')) out[k] = existing[k];
  }

  // OM-shaped sub-objects.
  for (const side of ['current', 'future']) {
    if (existing[side] && isObject(existing[side])) {
      const incSide = isObject(incoming[side]) ? incoming[side] : null;
      if (!incSide) {
        // Upload didn't bring this side at all — keep existing
        // entirely so any narratives / fields on that side survive.
        out[side] = existing[side];
      } else {
        out[side] = mergeOneSide(existing[side], incSide);
      }
    }
  }

  return out;
}

// Apply mergeSectionPreservingMeta across a map of sections.
// existing: { sectionKey: existingPayload }
// incoming: { sectionKey: incomingPayload }
// Returns a new map of merged payloads (only for keys in incoming).
export function mergeSectionMapPreservingMeta(existing, incoming) {
  const out = {};
  for (const [key, incomingPayload] of Object.entries(incoming || {})) {
    out[key] = mergeSectionPreservingMeta(existing?.[key], incomingPayload);
  }
  return out;
}

// Reports whether an existing section has any deep-dive content the
// merge will be protecting. Used to surface a "your deep-dives are
// safe" line in the upload review.
export function hasDeepDiveContent(existing) {
  if (!isObject(existing)) return false;
  if (existing._narrative || existing._explore_transcript) return true;
  for (const side of ['current', 'future']) {
    if (isObject(existing[side]) && (existing[side]._narrative || existing[side]._explore_transcript)) return true;
  }
  if (Array.isArray(existing.entries) && existing.entries.length > 0) return true;
  return false;
}

// Counts how many sections in an existing canvas map carry deep-dive
// content that will be protected during an upload merge.
export function countDeepDiveSections(existingMap) {
  if (!isObject(existingMap)) return 0;
  let count = 0;
  for (const [key, payload] of Object.entries(existingMap)) {
    if (key.startsWith('_') || key === 'lastUpdated') continue;
    if (hasDeepDiveContent(payload)) count++;
  }
  return count;
}
